/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { debounce, isEqual } from 'lodash';

import { isAuthenticatedUser } from 'js/lib/user';

import { Autocomplete, Button, IconButton, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { AIGenerateBrandedIcon } from '@coursera/cds-icons';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import { useIsComposablePageHeader } from 'bundles/page-header/contexts/PageHeaderContext';
import AutocompleteItemIcon from 'bundles/search-common/components/searchbar/AutocompleteItemIcon';
import AutocompleteOnboardingOption from 'bundles/search-common/components/searchbar/AutocompleteOnboardingOption';
import AutocompleteOptionTrackingDiv from 'bundles/search-common/components/searchbar/AutocompleteOptionTrackingDiv';
import AutocompleteRecentlySearchedOption from 'bundles/search-common/components/searchbar/AutocompleteRecentlySearchedOption';
import AutocompleteSearchProductCard from 'bundles/search-common/components/searchbar/AutocompleteSearchProductCard';
import AutocompleteTrendingOption from 'bundles/search-common/components/searchbar/AutocompleteTrendingOption';
import {
  AUTOCOMPLETE_DIRECT_MATCH_INDEX_ID,
  SEARCH_MAIN_INDEX_ID,
  SEARCH_SUGGESTIONS_INDEX_ID,
} from 'bundles/search-common/constants';
import {
  AUTOCOMPLETE_MODES,
  AUTOCOMPLETE_SECTION_PREFIXES,
  type AutocompleteMode,
  DEBOUNCE_DURATION_MS,
  LEARNER_FAVORITES_GROUP_ID,
  ONBOARDING_GROUP_ID,
  RECENTLY_VIEWED_GROUP_ID,
  RECENT_SEARCHES_GROUP_ID,
  TRENDING_SEARCHES_GROUP_ID,
  TRENDING_SEARCH_RESULTS_CONFIG,
  TRENDING_SEARCH_SUGGESTIONS_CONFIG,
} from 'bundles/search-common/constants/autocomplete';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import useSearchQuery from 'bundles/search-common/providers/useSearchQuery';
import { clearAllRecentSearches } from 'bundles/search-common/utils/SearchUtils';
import {
  type AutocompleteItemGroup,
  type AutocompleteItems,
  createAsYouTypeAutocompleteItems,
  createZeroStateAutocompleteItems,
} from 'bundles/search-common/utils/autocomplete';
import {
  getShowNewAutocomplete,
  impressIntoNewAutocompleteExperiment,
} from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/search-common';

type WrapperProps = {
  hideMobileSearchPage?: () => void;
  recentlySearchedItems?: AutocompleteItems;
  recentlyViewedItems?: AutocompleteItems;
  onRefreshItems?: () => void;
};

type Props = WrapperProps & {
  searchIsOpen: boolean;
  searchText: string;
  onInputChange: (value: string) => void;
  autocompleteMode: AutocompleteMode;
  onSubmit: (query: string) => void;
};

const styles = {
  autocomplete: (
    isComposablePageHeader?: boolean,
    trendingTitle?: string,
    isConsumerSearchPage?: boolean,
    isEqp?: boolean
  ) => css`
    .cds-popoverDropdown-paper {
      /* if we don't remove 2px from CDS, we have an awkward gap with page content */
      margin-top: var(--cds-spacing-100);
      padding: var(--cds-spacing-200);

      /* Override any max-width constraints from CDS */
      max-width: none !important;

      /* Ensure consistent positioning regardless of input content */
      position: absolute !important;
      transform-origin: top left !important;

      /* Responsive dropdown widths based on breakpoints */
      @media (min-width: ${breakpoints.values.sm}px) and (max-width: 1280px) {
        width: 548px !important;
      }

      /* For 1024-1060px when not consumer search and not EQP - make popover viewport width (override above) */
      ${!isConsumerSearchPage &&
      !isEqp &&
      css`
        @media (min-width: 1024px) and (max-width: 1060px) {
          width: 100vw !important;
          min-width: 100vw !important;
        }
      `}

      @media (min-width: 1281px) and (max-width: 1439px) {
        width: 802px !important;
      }

      @media (min-width: 1440px) {
        width: 1056px !important;
      }
    }

    /* Add padding to listbox on mobile/tablet */
    @media (max-width: ${breakpoints.values.md - 1}px) {
      [role='listbox'][aria-label='Suggestions'] {
        padding: var(--cds-spacing-300) var(--cds-spacing-200) 0 var(--cds-spacing-200) !important;
      }
    }

    .cds-multiSelectGroup-label {
      padding: 0 !important;
      margin: 0 0 var(--cds-spacing-150) 0 !important;
    }

    /* Base styling for multiSelectGroup-list - remove padding from MuiButtonBase-root */
    .cds-multiSelectGroup-list {
      gap: var(--cds-spacing-150) !important;

      > div[role='option'] {
        &.MuiButtonBase-root,
        &[class*='MuiButtonBase-root'] {
          padding: 0 !important;
        }
      }
    }

    /* Also target MuiButtonBase-root directly within multiSelectGroup-list with higher specificity */
    .cds-multiSelectGroup-list div[role='option'][class*='MuiButtonBase-root'] {
      padding: 0 !important;
    }

    /* Remove overflow and spacing between prefix and content */
    .cds-AutocompleteOption-prefix {
      overflow: visible !important;
      margin-right: 0 !important;
      padding-right: 0 !important;
    }

    .cds-AutocompleteOption-content {
      overflow: visible !important;
      margin-left: 0 !important;
      padding-left: 0 !important;
    }

    /* Remove any gap between prefix and content */
    .MuiButtonBase-root {
      .cds-AutocompleteOption-prefix + .cds-AutocompleteOption-content {
        margin-left: 0 !important;
        padding-left: 0 !important;
      }
    }

    /* Style for Trending Topics Group */
    ${trendingTitle &&
    css`
      div[role='group'][aria-label='${trendingTitle}'].cds-multiSelectGroup-list {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: var(--cds-spacing-100);

        > div[role='option'] {
          padding: 0 !important;
          background-color: transparent !important;
          display: inline-block !important;
          width: auto !important;
          min-width: auto !important;
        }

        button.cds-button-secondary,
        .cds-button-secondary {
          border: 1px solid var(--cds-color-grey-100) !important;
          color: var(--cds-color-grey-975) !important;
          box-shadow: none !important;
        }
      }
    `}

    /* Style for Recently Viewed / Learner Favorites Group */
    div[role='group'][aria-label*='Recently viewed'].cds-multiSelectGroup-list,
    div[role='group'][aria-label*='learner favorites'].cds-multiSelectGroup-list {
      display: flex;
      flex-direction: row;
      align-items: stretch; /* Make all children stretch to the same height */
      gap: var(--cds-spacing-100);

      > div[role='option'] {
        padding: 0 !important;
        background-color: transparent !important;
        display: flex !important;
        flex-direction: column;
        align-self: stretch; /* Ensure this element stretches to match parent height */

        /* Stretch MuiButtonBase-root - target the class directly */
        &.MuiButtonBase-root {
          align-self: stretch !important;
          display: flex !important;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0; /* Allow flex children to shrink below content size */
        }

        .cds-AutocompleteOption-content {
          > div {
            flex: 1;
          }
        }

        /* Mobile (< sm, < 600px): width 128px */
        @media (max-width: ${breakpoints.values.md - 1}px) {
          > div {
            width: 230px !important;
            max-width: 230px !important;
            display: flex;
            flex-direction: column;
            flex: 1;
          }
        }
      }

      /* Tablet and desktop (>= sm, >= 600px): width 246px */
      @media (min-width: ${breakpoints.values.md}px) {
        > div[role='option'] {
          width: 240px !important;
          min-width: 240px !important;
          max-width: 240px !important;

          > div {
            width: 240px !important;
            max-width: 240px !important;
            display: flex;
            flex-direction: column;
            flex: 1;
          }
        }
      }

      /* Mobile (< sm, < 600px): Make the section independently scrollable */
      @media (max-width: ${breakpoints.values.md - 1}px) {
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */

        /* Prevent cards from shrinking */
        > div[role='option'] {
          flex-shrink: 0 !important;
        }

        /* Hide scrollbar for webkit browsers (Chrome, Safari, etc.) */
        &::-webkit-scrollbar {
          display: none;
        }
      }
    }

    /* Style for Autocomplete Group Labels */
    .cds-multiSelectGroup-label {
      ${typography2.subtitleMedium}
    }

    /* Style for Recent Searches Group Label with Clear All */
    div[role='group'][aria-label='Recent searches'] .cds-multiSelectGroup-label {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      position: relative;
    }

    div[role='group'][aria-label='Recent searches'] .cds-multiSelectGroup-label::after {
      content: '';
      display: none;
    }

    .clear-all-recent-searches-button {
      ${typography2.actionSecondary}
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      margin-left: auto;
      padding-left: var(--cds-spacing-200);
      color: var(--cds-color-neutral-primary-weak) !important;

      &:hover {
        text-decoration: underline;
        background: none !important;
      }

      &:focus {
        outline: 2px solid var(--cds-color-blue-800);
        outline-offset: 2px;
        border-radius: var(--cds-border-radius-100);
      }
    }

    /* Visual separators - show space but hide the line */
    div[data-testid='visual-separator'],
    .cds-multiSelectGroup-divider {
      height: 20px;

      hr,
      .MuiDivider-root {
        background: none !important;
        padding: 0 !important;
        margin: 0 !important;
      }
    }

    /* temporarily hidden for the sake of experiment */

    .cds-SearchClearButton-searchClearButton,
    .cds-Search-clearPlaceholder {
      display: none;
    }

    &.cds-Search-base {
      margin: auto;
      margin-left: 4px;
      margin-top: ${isComposablePageHeader ? '0' : '8px'};
      height: 44px;
      width: 100%;
      max-width: ${isComposablePageHeader ? '480px' : 'none'};

      /* For 1024-1060px when not consumer search and not EQP - make search bar viewport width with padding */
      ${!isConsumerSearchPage &&
      !isEqp &&
      css`
        @media (min-width: 1024px) and (max-width: 1060px) {
          width: 95vw !important;
          max-width: 95vw !important;
          margin: var(--cds-spacing-100) !important;
        }
      `}

      @media (max-width: 1023px) {
        /* technically we show mobile header */
        width: ${isComposablePageHeader ? '100px' : '100%'};
        max-width: ${isComposablePageHeader ? '100px' : 'none'};
      }

      > div {
        border-radius: 24px;
        border: 1px solid var(--cds-color-grey-50);

        fieldset {
          /* remove duplicate border */
          border: none;
        }

        ::before {
          /* a11y border */
          border-radius: 24px !important;
        }
      }

      .cds-Search-prefix {
        display: none;
      }

      .cds-Search-suffix {
        /* for search button right spacing */
        padding-right: 4px;
      }

      /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
      input {
        margin-bottom: 0;
        border: 0;
        box-shadow: none;
        background: transparent;
      }

      /* necessary to remove inherited styles on account-profile (potentially elsewhere) */
      input[aria-invalid='true']:invalid:focus {
        box-shadow: none;
      }
    }

    & .cds-AutocompleteOption-suffix {
      display: none !important;
    }

    & .cds-AutocompleteOption-checked:not(:hover) {
      background: transparent !important;
    }
  `,
  searchButton: css`
    /* circular shape of search button */
    border-radius: 24px;
  `,
  hackyRetroButtonStyle: css`
    border-radius: 0;
  `,

  hackyRetroInputStyle: css`
    &.cds-Search-base {
      margin-top: 12px;
      margin-bottom: 12px;
      margin-left: 0;
      width: 100%;
      height: 40px;

      > div::before {
        border-radius: 0 !important;
      }

      input.cds-input-input {
        padding-left: 8px !important;
      }
    }

    .cds-Search-inputRoot {
      /* flex-direction: row-reverse; */
      border-radius: 0 !important;
      border: 1px solid var(--cds-color-grey-600) !important;
    }

    .cds-Search-prefix {
      padding: 0;
      margin: 0;
      align-items: start;
      position: relative;
      top: -1px;
      left: -2px;

      .cds-iconButton-medium {
        height: 40px;
        width: 38px;
        background-color: var(--cds-color-blue-800);
      }
    }
  `,
};

const SearchBarContent = (props: Props) => {
  const {
    searchIsOpen,
    hideMobileSearchPage,
    searchText,
    onSubmit,
    autocompleteMode,
    onInputChange,
    recentlySearchedItems = [],
    recentlyViewedItems = [],
    onRefreshItems,
  } = props;

  const isComposablePageHeader = useIsComposablePageHeader();
  const isMobileOrTabletView = useMediaQuery(breakpoints.down('sm'));

  const [isDropdownOpen, setIsDropdownOpen] = React.useState(!!searchIsOpen);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const isRemovingItemRef = React.useRef(false);
  const isConsumerSearchPage = useIsConsumerSerp();
  const isEqp = useIsEqp();
  const rootInputRef = React.useRef<HTMLInputElement>(null);
  const previousSearchTextRef = React.useRef<string>(searchText);
  const isUserLoggedIn = isAuthenticatedUser();
  const location = typeof window !== 'undefined' ? window.location : null;
  const referrer = location?.search ? `${location.pathname}${location.search}` : location?.pathname || '';
  const { onboardingUrl } = useBuildOnboardingUrl({ referrer });
  // a separate, durable query results set for the "trending" section of the autocomplete
  const { results: trendingSuggestions, error: trendingItemsError } = useSearchQuery(
    TRENDING_SEARCH_SUGGESTIONS_CONFIG,
    {
      skip: inServerContext,
    }
  );
  const { results: trendingProducts, error: trendingProductsError } = useSearchQuery(TRENDING_SEARCH_RESULTS_CONFIG, {
    skip: inServerContext || recentlyViewedItems.length >= 4,
  });
  // user input derived search results
  const { getSearchResults, updateQuery, isLoading, error: searchResultsError } = useSearch();
  const mainSearchData = getSearchResults(SEARCH_SUGGESTIONS_INDEX_ID);
  const directMatchResult = getSearchResults(AUTOCOMPLETE_DIRECT_MATCH_INDEX_ID);
  const aiSearchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { aiSearchMetadata } = aiSearchData || {};
  const isAISummaryEnabled = aiSearchMetadata?.isEligibleForAiGeneratedSummary && isConsumerSearchPage;
  const { elements: mainSearchDataElements, query: providerQuery } = mainSearchData || {};
  const isLoadingOrHasErrors = isLoading || trendingItemsError || trendingProductsError || searchResultsError;
  const showNewAutocomplete = getShowNewAutocomplete();

  // Close dropdown when view changes between mobile/tablet and desktop
  React.useEffect(() => {
    setIsDropdownOpen(false);
  }, [isMobileOrTabletView]);

  let showRetroStyles: boolean;
  if (showNewAutocomplete) {
    showRetroStyles = false;
  } else if (!isComposablePageHeader) {
    showRetroStyles = false;
  } else {
    showRetroStyles = !isAISummaryEnabled && isConsumerSearchPage && isMobileOrTabletView && !isDropdownOpen;
  }
  const is1061To1280 = useMediaQuery(`(min-width: ${breakpoints.values.sm}px) and (max-width: 1280px)`);
  const is1281To1440 = useMediaQuery('(min-width: 1281px) and (max-width: 1439px)');
  const is1440Plus = useMediaQuery('(min-width: 1440px)');

  // Calculate screen width based on breakpoints for createZeroStateAutocompleteItems
  const screenWidth = React.useMemo(() => {
    if (is1440Plus) return 1440;
    if (is1281To1440) return 1281;
    if (is1061To1280) return 1061;
    if (isMobileOrTabletView) return 1060;
    return undefined;
  }, [isMobileOrTabletView, is1061To1280, is1281To1440, is1440Plus]);

  // previous options - displayed during loading state
  const [prevOptions, setPrevOptions] = React.useState<AutocompleteItemGroup[]>([]);
  const autocompleteItemsInGroups = React.useMemo(() => {
    if (isLoadingOrHasErrors) return prevOptions;
    switch (autocompleteMode) {
      case AUTOCOMPLETE_MODES.AUTOCOMPLETE_ZERO_STATE:
        return createZeroStateAutocompleteItems(
          trendingSuggestions,
          isMobileOrTabletView,
          onboardingUrl,
          isUserLoggedIn,
          recentlySearchedItems,
          recentlyViewedItems,
          screenWidth,
          trendingProducts
        );
      case AUTOCOMPLETE_MODES.AUTOCOMPLETE_AS_YOU_TYPE:
        return createAsYouTypeAutocompleteItems(
          mainSearchDataElements,
          searchText,
          directMatchResult?.elements,
          onboardingUrl,
          isUserLoggedIn
        );
      default:
        return [];
    }
  }, [
    autocompleteMode,
    mainSearchDataElements,
    trendingSuggestions,
    trendingProducts,
    searchText,
    prevOptions,
    isLoadingOrHasErrors,
    recentlyViewedItems,
    recentlySearchedItems,
    directMatchResult?.elements,
    isMobileOrTabletView,
    onboardingUrl,
    isUserLoggedIn,
    screenWidth,
  ]);
  const trendingGroup = autocompleteItemsInGroups.find((g) => g.id === TRENDING_SEARCHES_GROUP_ID);
  const trendingTitle = trendingGroup?.title;

  const handleRemoveRecentSearch = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    setRefreshKey((prev) => prev + 1);
    // Trigger parent to refresh items
    onRefreshItems?.();
    // Keep dropdown open by refocusing the input
    setTimeout(() => {
      rootInputRef.current?.focus?.();
      // Reset the flag after a short delay to allow any pending selection events to be skipped
      setTimeout(() => {
        isRemovingItemRef.current = false;
      }, 100);
    }, 0);
  }, [onRefreshItems]);

  const handleRemoveRecentlyViewed = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    setRefreshKey((prev) => prev + 1);
    // Trigger parent to refresh items
    onRefreshItems?.();
    // Keep dropdown open by refocusing the input
    setTimeout(() => {
      rootInputRef.current?.focus?.();
      // Reset the flag after a short delay to allow any pending selection events to be skipped
      setTimeout(() => {
        isRemovingItemRef.current = false;
      }, 100);
    }, 0);
  }, [onRefreshItems]);

  const handleClearAllRecentSearches = React.useCallback<() => void>(() => {
    isRemovingItemRef.current = true;
    clearAllRecentSearches();
    setRefreshKey((prev) => prev + 1);
    // Trigger parent to refresh items
    onRefreshItems?.();
    // Keep dropdown open by refocusing the input
    setTimeout(() => {
      rootInputRef.current?.focus?.();
      // Reset the flag after a short delay to allow any pending selection events to be skipped
      setTimeout(() => {
        isRemovingItemRef.current = false;
      }, 100);
    }, 0);
  }, [onRefreshItems]);

  const refineSearch = (newValue: string) => {
    updateQuery(newValue);
  };
  const debouncedRefineSearch = debounce(refineSearch, DEBOUNCE_DURATION_MS);

  // When the response is loading, we want to persist the previous options
  React.useEffect(() => {
    if (!isLoadingOrHasErrors && !isEqual(autocompleteItemsInGroups, prevOptions)) {
      setPrevOptions(autocompleteItemsInGroups);
    }
  }, [autocompleteItemsInGroups, prevOptions, isLoadingOrHasErrors]);

  // Update the query in the provider when the search text changes
  React.useEffect(() => {
    if (!isLoadingOrHasErrors && searchText && searchText !== providerQuery) {
      debouncedRefineSearch(searchText);
    }
  }, [searchText, debouncedRefineSearch, providerQuery, isLoadingOrHasErrors]);

  const handleSubmit = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (value: string) => {
      onSubmit(decodeURIComponent(value));
      // If we blur immediately, the text in the input will not update on selection
      setTimeout(() => {
        rootInputRef.current?.blur?.();
      });
    },
    [onSubmit]
  );
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_autocomplete_suggestions',
    { searchSuggestions: 'TestSearch' }
  );

  // Determine the prefix icon based on styles and AI summary state
  let prefixIcon;
  if (showRetroStyles) {
    prefixIcon = (
      <IconButton
        intent="search"
        size="medium"
        css={[styles.searchButton, styles.hackyRetroButtonStyle]}
        type="submit"
      />
    );
  } else if (isAISummaryEnabled) {
    prefixIcon = <AIGenerateBrandedIcon aria-label={_t('AI Search')} />;
  }

  return (
    <div className="rc-SearchBarContent">
      <Autocomplete
        popoverTrigger={(() => {
          // For 1024-1060px breakpoint when not on EQP or SERP, use "input" trigger so it only opens when user types, not on focus
          if (!isConsumerSearchPage && !isEqp) {
            const is1024To1060 =
              typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px) and (max-width: 1060px)').matches;
            if (is1024To1060) {
              return 'input' as const;
            }
          }
          return 'focus' as const;
        })()}
        id="search-autocomplete"
        name="query"
        placeholder={isAISummaryEnabled ? _t('Ask Coursera AI or Search') : _t('What do you want to learn?')}
        items={autocompleteItemsInGroups}
        value={searchText}
        allowsCustomValue
        defaultValue={searchText}
        prefixIcon={prefixIcon}
        css={[
          styles.autocomplete(isComposablePageHeader, trendingTitle, isConsumerSearchPage, isEqp),
          showRetroStyles && styles.hackyRetroInputStyle,
        ]}
        inputActionButtons={
          !showRetroStyles && <IconButton intent="search" size="small" css={[styles.searchButton]} type="submit" />
        }
        onInputChange={(value) => {
          // Store previous search text before updating
          previousSearchTextRef.current = searchText;
          onInputChange(value);
        }}
        disableSelectionChangeOnTab
        onSelectionChange={(selectedOptionText) => {
          // Prevent the autocomplete from reopening after selection
          setIsDropdownOpen(false);

          // Skip selection if we just removed an item
          if (isRemovingItemRef.current) {
            return;
          }
          if (selectedOptionText) {
            const selectedText = String(selectedOptionText);
            let shouldPreventInputUpdate = false;

            if (onboardingUrl && selectedText.startsWith(`${AUTOCOMPLETE_SECTION_PREFIXES.ONBOARDING}~`)) {
              shouldPreventInputUpdate = true;
              // Restore previous search text to prevent input update
              if (rootInputRef.current) {
                rootInputRef.current.value = previousSearchTextRef.current;
              }
              onInputChange(previousSearchTextRef.current);
              window.location.assign(onboardingUrl);
              return;
            }

            // Find the matching item and determine action based on its ID prefix
            for (const group of autocompleteItemsInGroups) {
              const matchingItem = group.children.find(
                (item) => item.textValue === selectedText || item.name === selectedText
              );

              if (matchingItem?.id) {
                // Determine prefix from the matching item's ID
                const prefix = Object.values(AUTOCOMPLETE_SECTION_PREFIXES).find((p) => matchingItem.id?.startsWith(p));

                switch (prefix) {
                  case AUTOCOMPLETE_SECTION_PREFIXES.RECENTLY_VIEWED:
                  case AUTOCOMPLETE_SECTION_PREFIXES.DIRECT_MATCHES:
                    shouldPreventInputUpdate = true;
                    // Restore previous search text to prevent input update
                    if (rootInputRef.current) {
                      rootInputRef.current.value = previousSearchTextRef.current;
                    }
                    onInputChange(previousSearchTextRef.current);
                    // Recently viewed items or trending products in learner favorites - navigate to path
                    if (matchingItem.path) {
                      window.location.assign(matchingItem.path);
                      return;
                    }
                    break;

                  default:
                    // For all other items (trending, as you type, etc.), submit as search query
                    handleSubmit(encodeURIComponent(String(selectedOptionText)));
                    return;
                }
              }
            }

            // If no item found, submit as search query
            if (!shouldPreventInputUpdate) {
              handleSubmit(encodeURIComponent(String(selectedOptionText)));
            }
          }
        }}
        inputProps={{
          id: 'search-autocomplete-input',
          'data-testid': 'search-autocomplete-input',
          name: 'query',
          type: 'text',
          onKeyUp: (e) => {
            if (e.key === 'Enter') {
              handleSubmit(encodeURIComponent(searchText));
            }
          },
        }}
        ref={rootInputRef}
        autoFocus={isDropdownOpen}
        data-testid="search-autocomplete"
        aria-label={_t('Search catalog')}
        dropdownProps={{
          disablePortal: false,
          style: {
            width: isComposablePageHeader ? '1000px' : '1000px',
            maxHeight: 'calc(100vh - 120px)',
          },
        }}
        drawerInputProps={{
          enterkeyhint: 'Search',
          onKeyUp: (e) => {
            if (e.key === 'Enter') {
              handleSubmit(encodeURIComponent(searchText));
            }
          },
        }}
        onClose={() => {
          setIsDropdownOpen(false);
          hideMobileSearchPage?.();
        }}
        onOpen={() => {
          setIsDropdownOpen(true);
          impressIntoNewAutocompleteExperiment();
        }}
      >
        {({ title: groupTitle, children, id: groupId }) => {
          if (groupId === TRENDING_SEARCHES_GROUP_ID) {
            return (
              <Autocomplete.Group key={groupTitle} items={children} title={groupTitle} aria-label={groupTitle}>
                {children.map(({ id, supportText, name, textValue }, idx) => (
                  <Autocomplete.Option
                    // @ts-expect-error: This does nothing
                    ref={visibilityTrackingRef}
                    key={name}
                    supportText={supportText}
                    textValue={textValue ?? name}
                    aria-label={name}
                  >
                    <AutocompleteTrendingOption
                      autocompleteState={autocompleteMode}
                      id={id}
                      recentlyViewedItems={recentlyViewedItems}
                      suggestionIndex={idx}
                    >
                      {name}
                    </AutocompleteTrendingOption>
                  </Autocomplete.Option>
                ))}
              </Autocomplete.Group>
            );
          }

          if (groupId === ONBOARDING_GROUP_ID) {
            const onboardingItem = children[0];
            if (!onboardingItem) return null;

            return (
              <Autocomplete.Group key={ONBOARDING_GROUP_ID} items={children} title="" aria-label={_t('Onboarding')}>
                <Autocomplete.Option
                  // @ts-expect-error: This does nothing
                  ref={visibilityTrackingRef}
                  key={onboardingItem.id}
                  textValue={onboardingItem.textValue ?? onboardingItem.name}
                  aria-label={onboardingItem.name}
                >
                  <AutocompleteOnboardingOption
                    autocompleteState={autocompleteMode}
                    id={onboardingItem.id}
                    recentlyViewedItems={recentlyViewedItems}
                    suggestionIndex={0}
                    path={onboardingItem.path}
                  />
                </Autocomplete.Option>
              </Autocomplete.Group>
            );
          }

          if (groupId === RECENTLY_VIEWED_GROUP_ID || groupId === LEARNER_FAVORITES_GROUP_ID) {
            const productCards = children
              .map((item, idx) => {
                return (
                  <Autocomplete.Option
                    // @ts-expect-error: This does nothing
                    ref={visibilityTrackingRef}
                    key={item.name}
                    textValue={item.name}
                    aria-label={item.name}
                  >
                    <AutocompleteSearchProductCard
                      autocompleteState={autocompleteMode}
                      id={item.id || ''}
                      recentlyViewedItems={recentlyViewedItems}
                      suggestionIndex={idx}
                      item={item}
                      groupId={groupId}
                      onRemove={handleRemoveRecentlyViewed}
                    />
                  </Autocomplete.Option>
                );
              })
              .filter((card): card is React.ReactElement => card !== null);

            return (
              <Autocomplete.Group
                key={
                  groupId === RECENTLY_VIEWED_GROUP_ID || groupId === LEARNER_FAVORITES_GROUP_ID
                    ? `${groupTitle}-${refreshKey}`
                    : groupTitle
                }
                items={children}
                title={groupTitle}
                aria-label={groupTitle}
              >
                {productCards}
              </Autocomplete.Group>
            );
          }

          return (
            <Autocomplete.Group
              key={groupId === RECENT_SEARCHES_GROUP_ID ? `${groupTitle}-${refreshKey}` : groupTitle}
              items={children}
              // @ts-expect-error: Autocomplete.Group title prop expects string, but we need to render a button alongside the title
              title={
                groupId === RECENT_SEARCHES_GROUP_ID ? (
                  <div
                    css={css`
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      width: 100%;
                    `}
                  >
                    <span>{groupTitle}</span>
                    <Button
                      variant="ghostSecondary"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleClearAllRecentSearches();
                      }}
                      className="clear-all-recent-searches-button"
                      aria-label={_t('Clear all recent searches')}
                    >
                      {_t('Clear all')}
                    </Button>
                  </div>
                ) : (
                  groupTitle
                )
              }
            >
              {children.map(({ imageUrl, id, supportText, name, textValue }, idx) => (
                <Autocomplete.Option
                  // @ts-expect-error: This does nothing
                  ref={visibilityTrackingRef}
                  prefix={<AutocompleteItemIcon imageUrl={imageUrl} groupId={groupId} avatarAltText={name} />}
                  key={name}
                  supportText={supportText}
                  textValue={textValue ?? name}
                  aria-label={name}
                >
                  {groupId === RECENT_SEARCHES_GROUP_ID ? (
                    <AutocompleteRecentlySearchedOption
                      autocompleteState={autocompleteMode}
                      id={id}
                      recentlyViewedItems={recentlyViewedItems}
                      suggestionIndex={idx}
                      onRemove={handleRemoveRecentSearch}
                    >
                      {name}
                    </AutocompleteRecentlySearchedOption>
                  ) : (
                    <AutocompleteOptionTrackingDiv
                      autocompleteState={autocompleteMode}
                      id={id}
                      recentlyViewedItems={recentlyViewedItems}
                      suggestionIndex={idx}
                      searchText={providerQuery}
                    >
                      {name}
                    </AutocompleteOptionTrackingDiv>
                  )}
                </Autocomplete.Option>
              ))}
            </Autocomplete.Group>
          );
        }}
      </Autocomplete>
    </div>
  );
};

export default SearchBarContent;
